"use client";

import type * as React from "react";
import { useState } from "react";
import type { PropsBase } from "@daypicker/react";
import { CalendarIcon } from "lucide-react";
import { Button } from "#ui-atoms/atoms/button";
import type { BaseButtonProps } from "#ui-atoms/atoms/button/button";
import { Calendar } from "#ui-atoms/atoms/calendar";
import { Popover, PopoverPopup, PopoverTrigger } from "#ui-atoms/atoms/popover";
import { cn } from "#ui-atoms/lib/utils";

// coss ui Date Picker (https://coss.com/ui/docs/components/date-picker):
// a composition of Popover + Button + Calendar, single-date mode. The
// Calendar's own props — `disabled` matchers, `startMonth`/`endMonth`,
// `locale`, `captionLayout`… — pass through `calendar`.

export interface DatePickerProps {
  /** Selected date (controlled); `null` keeps it controlled with nothing selected. */
  value?: Date | null;
  /** Initial date (uncontrolled). */
  defaultValue?: Date;
  onValueChange?: (date: Date | undefined) => void;
  /** Trigger label while nothing is selected. */
  placeholder?: React.ReactNode;
  /**
   * Formats the selected date for the trigger. Defaults to the viewer's
   * locale long date (`Intl.DateTimeFormat`, `dateStyle: "long"`).
   */
  formatValue?: (date: Date) => string;
  /** Close the popover once a date is picked. */
  closeOnSelect?: boolean;
  /**
   * Keep a date selected once picked: clicking the selected day fires
   * `onValueChange` with it again instead of clearing the selection.
   */
  required?: boolean;
  disabled?: boolean;
  /** Everything the Calendar accepts except the selection props. */
  calendar?: Omit<PropsBase, "mode" | "selected" | "onSelect" | "required">;
  /** Trigger button props: `className`, `size`, `variant`, `aria-label`, `id`… */
  trigger?: Omit<
    BaseButtonProps,
    "children" | "disabled" | "render" | "tenant"
  >;
  /** Popover placement, relative to the trigger. */
  align?: React.ComponentProps<typeof PopoverPopup>["align"];
  side?: React.ComponentProps<typeof PopoverPopup>["side"];
}

function defaultFormatValue(date: Date): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "long" }).format(date);
}

export function DatePicker({
  value,
  defaultValue,
  onValueChange,
  placeholder = "Pick a date",
  formatValue = defaultFormatValue,
  closeOnSelect = true,
  required = false,
  disabled = false,
  calendar,
  trigger,
  align = "start",
  side = "bottom",
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [uncontrolledValue, setUncontrolledValue] = useState<Date | undefined>(
    defaultValue,
  );
  const isControlled = value !== undefined;
  const selected = isControlled ? (value ?? undefined) : uncontrolledValue;

  const handleSelect = (date: Date | undefined) => {
    if (!isControlled) setUncontrolledValue(date);
    onValueChange?.(date);
    if (closeOnSelect && date) setOpen(false);
  };

  const { className: triggerClassName, ...triggerProps } = trigger ?? {};
  // DayPicker discriminates its single-mode props on `required`, so the
  // selection props are built per branch rather than passed as a boolean.
  const selection = required
    ? {
        mode: "single" as const,
        required: true as const,
        selected,
        onSelect: handleSelect,
      }
    : {
        mode: "single" as const,
        required: false as const,
        selected,
        onSelect: handleSelect,
      };

  return (
    <Popover onOpenChange={setOpen} open={open}>
      <PopoverTrigger
        data-placeholder={selected ? undefined : ""}
        data-slot="date-picker-trigger"
        disabled={disabled}
        render={
          <Button
            className={cn(
              "w-full justify-start font-normal data-placeholder:text-muted-foreground",
              triggerClassName,
            )}
            variant="outline"
            {...triggerProps}
          />
        }
      >
        <CalendarIcon aria-hidden="true" />
        {selected ? formatValue(selected) : placeholder}
      </PopoverTrigger>
      <PopoverPopup align={align} className="w-auto" side={side}>
        <Calendar
          {...calendar}
          defaultMonth={selected ?? calendar?.defaultMonth}
          {...selection}
        />
      </PopoverPopup>
    </Popover>
  );
}
